import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Briefcase, RefreshCw, Plus, Edit3, Trash2, Eye, EyeOff,
  Search, Download, FileText, Loader2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  'new', 'under_review', 'shortlisted', 'interview', 'offer', 'rejected', 'hired',
];

const EMPTY_JOB = {
  title: '', department: '', location: '', employment_type: 'Full-Time',
  salary: '', working_hours: '', reference: '', short_description: '',
  description: '',
  responsibilities: '', skills: '', benefits: '',
  closing_date: '', is_published: false,
};

function splitLines(s) {
  return (s || '').split('\n').map((x) => x.trim()).filter(Boolean);
}
function joinLines(arr) {
  return Array.isArray(arr) ? arr.join('\n') : (arr || '');
}

/**
 * Careers Admin Panel — jobs + applications management for Flight380.
 */
export const CareersPanel = () => {
  const [tab, setTab] = useState('jobs');
  return (
    <div data-testid="admin-careers-panel">
      <div className="flex items-center gap-2 mb-4">
        <Button
          variant={tab === 'jobs' ? 'default' : 'outline'}
          onClick={() => setTab('jobs')}
          data-testid="admin-careers-tab-jobs"
          className={tab === 'jobs' ? 'bg-[#FF6600] hover:bg-[#E65C00] text-white' : ''}
        >
          <Briefcase className="h-4 w-4 mr-2" /> Jobs
        </Button>
        <Button
          variant={tab === 'apps' ? 'default' : 'outline'}
          onClick={() => setTab('apps')}
          data-testid="admin-careers-tab-apps"
          className={tab === 'apps' ? 'bg-[#FF6600] hover:bg-[#E65C00] text-white' : ''}
        >
          <FileText className="h-4 w-4 mr-2" /> Applications
        </Button>
      </div>
      {tab === 'jobs' ? <JobsSection /> : <ApplicationsSection />}
    </div>
  );
};

/* ---------------- Jobs ---------------- */

function JobsSection() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // job object or EMPTY_JOB
  const [error, setError] = useState('');

  async function load() {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/admin/careers/jobs`);
      setJobs(res.data?.jobs || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load jobs.');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  async function togglePublish(job) {
    try {
      await axios.post(`${API}/admin/careers/jobs/${job.id}/publish`, {
        publish: !job.is_published,
      });
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Failed to toggle publish.'); }
  }
  async function remove(job) {
    if (!window.confirm(`Delete "${job.title}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/careers/jobs/${job.id}`);
      await load();
    } catch (e) { alert(e?.response?.data?.detail || 'Failed to delete.'); }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Briefcase className="h-5 w-5 text-[#FF6600]" /> Job openings
        </CardTitle>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
          <Button
            size="sm"
            onClick={() => setEditing({ ...EMPTY_JOB })}
            data-testid="admin-careers-new-job"
            className="bg-[#FF6600] hover:bg-[#E65C00] text-white"
          >
            <Plus className="h-4 w-4 mr-1" /> New job
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
        {error && <div className="text-sm text-red-600">{error}</div>}
        {!loading && jobs.length === 0 && (
          <div className="text-sm text-slate-500">No jobs yet. Create the first one with <strong>New job</strong>.</div>
        )}
        {!loading && jobs.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left border-b">
                <tr className="text-slate-600">
                  <th className="py-2 pr-3">Title</th>
                  <th className="py-2 pr-3">Location</th>
                  <th className="py-2 pr-3">Type</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.id} className="border-b hover:bg-slate-50" data-testid={`admin-job-row-${j.slug}`}>
                    <td className="py-2 pr-3 font-medium">{j.title}<div className="text-xs text-slate-500">{j.reference || j.slug}</div></td>
                    <td className="py-2 pr-3">{j.location}</td>
                    <td className="py-2 pr-3">{j.employment_type}</td>
                    <td className="py-2 pr-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${j.is_published ? 'bg-[#FFF3E8] text-[#E65C00]' : 'bg-slate-100 text-slate-600'}`}>
                        {j.is_published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td className="py-2 pr-3 flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => setEditing(j)}>
                        <Edit3 className="h-3 w-3 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => togglePublish(j)}>
                        {j.is_published ? <><EyeOff className="h-3 w-3 mr-1" /> Unpublish</> : <><Eye className="h-3 w-3 mr-1" /> Publish</>}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => remove(j)} className="text-red-600">
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      {editing && (
        <JobEditor
          job={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </Card>
  );
}

function JobEditor({ job, onClose, onSaved }) {
  const isNew = !job.id;
  const [values, setValues] = useState({
    ...EMPTY_JOB,
    ...job,
    responsibilities: joinLines(job.responsibilities),
    skills: joinLines(job.skills),
    benefits: joinLines(job.benefits),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  function set(k, v) { setValues((s) => ({ ...s, [k]: v })); }

  async function save() {
    setError('');
    setSaving(true);
    const payload = {
      ...values,
      responsibilities: splitLines(values.responsibilities),
      skills: splitLines(values.skills),
      benefits: splitLines(values.benefits),
    };
    try {
      if (isNew) {
        await axios.post(`${API}/admin/careers/jobs`, payload);
      } else {
        await axios.put(`${API}/admin/careers/jobs/${job.id}`, payload);
      }
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-[300] flex items-start justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-3xl max-h-[100vh] sm:max-h-[90vh] overflow-y-auto rounded-none sm:rounded-xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-white z-10">
          <h3 className="text-lg font-bold">{isNew ? 'New job' : 'Edit job'}</h3>
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
        <div className="p-6 space-y-4">
          {error && <div className="text-sm text-red-600">{error}</div>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <EField label="Title *" value={values.title} onChange={(v) => set('title', v)} />
            <EField label="Slug (URL)" value={values.slug || ''} onChange={(v) => set('slug', v)} placeholder="e.g. b2b-sales-executive-umrah" />
            <EField label="Department" value={values.department} onChange={(v) => set('department', v)} />
            <EField label="Location" value={values.location} onChange={(v) => set('location', v)} />
            <EField label="Employment type" value={values.employment_type} onChange={(v) => set('employment_type', v)} />
            <EField label="Salary" value={values.salary} onChange={(v) => set('salary', v)} />
            <EField label="Working hours" value={values.working_hours} onChange={(v) => set('working_hours', v)} />
            <EField label="Reference" value={values.reference} onChange={(v) => set('reference', v)} />
            <EField label="Closing date" type="date" value={values.closing_date} onChange={(v) => set('closing_date', v)} />
            <label className="flex items-center gap-2 mt-6">
              <input type="checkbox" checked={!!values.is_published} onChange={(e) => set('is_published', e.target.checked)} />
              <span>Published (visible on /careers)</span>
            </label>
          </div>
          <EField label="Short description" value={values.short_description} onChange={(v) => set('short_description', v)} />
          <ETextarea label="Full description" rows={5} value={values.description} onChange={(v) => set('description', v)} />
          <ETextarea label="Responsibilities (one per line)" rows={5} value={values.responsibilities} onChange={(v) => set('responsibilities', v)} />
          <ETextarea label="Skills & experience (one per line)" rows={5} value={values.skills} onChange={(v) => set('skills', v)} />
          <ETextarea label="Benefits (one per line)" rows={4} value={values.benefits} onChange={(v) => set('benefits', v)} />
          <div className="flex gap-2 pt-2">
            <Button onClick={save} disabled={saving} className="bg-[#FF6600] hover:bg-[#E65C00] text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              {isNew ? 'Create job' : 'Save changes'}
            </Button>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function EField({ label, value, onChange, type = 'text', placeholder = '' }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-600 mb-1">{label}</span>
      <input
        type={type}
        value={value || ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6600]/40 focus:border-[#FF6600]"
      />
    </label>
  );
}
function ETextarea({ label, value, onChange, rows = 4 }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-600 mb-1">{label}</span>
      <textarea
        rows={rows}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FF6600]/40 focus:border-[#FF6600]"
      />
    </label>
  );
}


/* ---------------- Applications ---------------- */

function ApplicationsSection() {
  const [apps, setApps] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterJob, setFilterJob] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);

  async function load() {
    setLoading(true);
    const params = {};
    if (filterJob) params.job_id = filterJob;
    if (filterStatus) params.status = filterStatus;
    if (q) params.q = q;
    try {
      const [appsRes, jobsRes] = await Promise.all([
        axios.get(`${API}/admin/careers/applications`, { params }),
        jobs.length ? Promise.resolve({ data: { jobs } }) : axios.get(`${API}/admin/careers/jobs`),
      ]);
      setApps(appsRes.data?.applications || []);
      if (!jobs.length) setJobs(jobsRes.data?.jobs || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filterJob, filterStatus]);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-[#FF6600]" /> Applications ({apps.length})
        </CardTitle>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="h-4 w-4 mr-1" /> Refresh
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <select value={filterJob} onChange={(e) => setFilterJob(e.target.value)} className="px-3 py-2 border rounded-lg">
            <option value="">All jobs</option>
            {jobs.map((j) => <option key={j.id} value={j.id}>{j.title}</option>)}
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-3 py-2 border rounded-lg">
            <option value="">Any status</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </select>
          <div className="sm:col-span-2 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && load()}
                placeholder="Search by name or email"
                className="w-full pl-8 pr-3 py-2 border rounded-lg"
              />
            </div>
            <Button onClick={load} variant="outline">Search</Button>
          </div>
        </div>
        {loading && <div className="text-sm text-slate-500">Loading…</div>}
        {!loading && apps.length === 0 && <div className="text-sm text-slate-500">No applications yet.</div>}
        {!loading && apps.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left border-b text-slate-600">
                <tr>
                  <th className="py-2 pr-3">Applicant</th>
                  <th className="py-2 pr-3">Job</th>
                  <th className="py-2 pr-3">Received</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {apps.map((a) => (
                  <tr key={a.id} className="border-b hover:bg-slate-50">
                    <td className="py-2 pr-3">
                      <div className="font-medium">{a.full_name}</div>
                      <div className="text-xs text-slate-500">{a.email}</div>
                    </td>
                    <td className="py-2 pr-3">{a.job_title}</td>
                    <td className="py-2 pr-3">{new Date(a.created_at).toLocaleString()}</td>
                    <td className="py-2 pr-3">
                      <span className="px-2 py-0.5 rounded-full text-xs bg-slate-100">{a.status.replace('_', ' ')}</span>
                    </td>
                    <td className="py-2 pr-3 flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => setSelected(a)}>Open</Button>
                      <a href={`${API}/admin/careers/applications/${a.id}/cv`} target="_blank" rel="noreferrer">
                        <Button size="sm" variant="outline"><Download className="h-3 w-3 mr-1" /> CV</Button>
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      {selected && (
        <ApplicationDrawer app={selected} onClose={() => setSelected(null)} onSaved={(a) => { setSelected(a); load(); }} />
      )}
    </Card>
  );
}

function ApplicationDrawer({ app, onClose, onSaved }) {
  const [status, setStatus] = useState(app.status);
  const [notes, setNotes] = useState(app.internal_notes || '');
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/admin/careers/applications/${app.id}/status`, {
        status, internal_notes: notes,
      });
      onSaved(res.data.application);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-[300] flex items-start justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-2xl max-h-[100vh] sm:max-h-[90vh] overflow-y-auto rounded-none sm:rounded-xl" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 border-b flex justify-between items-center">
          <div>
            <h3 className="text-lg font-bold">{app.full_name}</h3>
            <p className="text-sm text-slate-500">{app.job_title} · {new Date(app.created_at).toLocaleString()}</p>
          </div>
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
        <div className="p-6 space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div><div className="text-xs text-slate-500">Email</div>{app.email}</div>
            <div><div className="text-xs text-slate-500">Phone</div>{app.phone}</div>
            <div><div className="text-xs text-slate-500">Location</div>{app.location || '—'}</div>
            <div><div className="text-xs text-slate-500">Right to work</div>{({
              yes: 'Yes, unrestricted',
              visa: 'Yes, with a valid visa',
              sponsorship: 'Sponsorship required',
              no: 'No, sponsorship required',
            })[app.right_to_work] || app.right_to_work || '—'}</div>
            <div className="col-span-2"><div className="text-xs text-slate-500">LinkedIn</div>{app.linkedin || '—'}</div>
          </div>
          <div>
            <div className="text-xs text-slate-500 mb-1">Cover letter</div>
            <div className="whitespace-pre-line border rounded-lg p-3 bg-slate-50">{app.cover_letter || '—'}</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs font-semibold text-slate-600 mb-1">Status</span>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full px-3 py-2 border rounded-lg">
                {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
            </label>
            <a href={`${API}/admin/careers/applications/${app.id}/cv`} target="_blank" rel="noreferrer" className="self-end">
              <Button variant="outline"><Download className="h-4 w-4 mr-1" /> Download CV</Button>
            </a>
          </div>
          <label className="block">
            <span className="block text-xs font-semibold text-slate-600 mb-1">Internal notes</span>
            <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full px-3 py-2 border rounded-lg" />
          </label>
          <div className="flex gap-2">
            <Button onClick={save} disabled={saving} className="bg-[#FF6600] hover:bg-[#E65C00] text-white">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Save changes
            </Button>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CareersPanel;
